import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, ShieldCheck, ExternalLink, Webhook, Mail, Key, Brain, Send, Loader2, PlayCircle, CircleDashed, Landmark } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";

const Row = ({ ok, label, hint, testId }) => (
  <div data-testid={testId} className={`flex items-start gap-3 p-4 rounded-2xl border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
    <div className="flex-1">
      <div className={`text-sm font-semibold ${ok ? "text-emerald-900" : "text-amber-900"}`}>{label}</div>
      {hint && <div className="text-xs text-slate-600 mt-1">{hint}</div>}
    </div>
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${ok ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"}`}>{ok ? "Yes" : "No"}</span>
  </div>
);

const Detail = ({ title, value, status }) => {
  const tone = status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-700" : status === "bad" ? "text-rose-700" : "text-slate-600";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{title}</div>
      <div className={`text-xs font-mono mt-1 break-all ${tone}`}>{value || "—"}</div>
    </div>
  );
};

export default function AdminReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [guardrails, setGuardrails] = useState(null);
  const [guardrailsRunning, setGuardrailsRunning] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const reload = () => {
    setLoading(true);
    setLoadError("");
    api.get("/admin/readiness")
      .then(({ data }) => setData(data))
      .catch((err) => {
        // Feb 2026 — surface the real reason instead of leaving the
        // page stuck on an infinite "Loading…" spinner. Common causes:
        // stale JWT, backend 500, or a stale bundle after a deploy.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || "Request failed";
        setLoadError(
          status === 401 || status === 403
            ? "Your admin session has expired. Please sign in again."
            : `Couldn't load the readiness data (HTTP ${status || "?"} — ${detail}).`,
        );
      })
      .finally(() => setLoading(false));
    api.get("/admin/ai-summaries/regression-status").then(({ data }) => setGuardrails(data)).catch(() => {});
    api.get("/admin/platform-settings").then(({ data }) => setPlatformSettings(data)).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  const togglePermission = async (key, value) => {
    setSettingsSaving(true);
    try {
      const { data } = await api.patch("/admin/platform-settings", { [key]: value });
      setPlatformSettings(data);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Phase 2L-A7: bank-transfer details for invoice bookings. Local form
  // state so admins can type freely; we only PATCH on Save. Read access is
  // lab-staff (so lab tech sees the same values that the invoice email
  // would render) but PATCH stays admin-only (enforced server-side too).
  const [bankForm, setBankForm] = useState({
    bank_account_name: "", bank_bsb: "", bank_account_number: "",
    bank_name: "", bank_payment_reference: "",
  });
  const [bankSaving, setBankSaving] = useState(false);
  const [bankSaveOk, setBankSaveOk] = useState(false);
  useEffect(() => {
    if (platformSettings) {
      setBankForm({
        bank_account_name:      platformSettings.bank_account_name || "",
        bank_bsb:               platformSettings.bank_bsb || "",
        bank_account_number:    platformSettings.bank_account_number || "",
        bank_name:              platformSettings.bank_name || "",
        bank_payment_reference: platformSettings.bank_payment_reference || "",
      });
    }
  }, [platformSettings]);
  const saveBank = async () => {
    setBankSaving(true);
    setBankSaveOk(false);
    try {
      const { data } = await api.patch("/admin/platform-settings", bankForm);
      setPlatformSettings(data);
      setBankSaveOk(true);
      setTimeout(() => setBankSaveOk(false), 3000);
    } finally {
      setBankSaving(false);
    }
  };
  const bankConfigured = Boolean(
    platformSettings?.bank_account_name &&
    platformSettings?.bank_bsb &&
    platformSettings?.bank_account_number
  );

  const runGuardrailsCheck = async () => {
    if (guardrailsRunning) return;
    setGuardrailsRunning(true);
    try {
      const { data: res } = await api.post("/admin/ai-summaries/regression-status/run");
      setGuardrails(res);
    } catch (err) {
      setGuardrails({
        status: "error",
        error: err?.response?.data?.detail || err?.message || "Request failed",
        checked_at: new Date().toISOString(),
        failed_checks: [],
        total_passed: 0,
        total_failed: 0,
      });
    } finally {
      setGuardrailsRunning(false);
    }
  };

  const sendTestBookingConfirmation = async () => {
    if (!testTo || testSending) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const { data: res } = await api.post("/admin/emails/test-booking-confirmation", { to: testTo });
      setTestResult(res);
    } catch (err) {
      setTestResult({
        sent: false,
        status: "failed",
        error: err?.response?.data?.detail || err?.message || "Request failed",
      });
    } finally {
      setTestSending(false);
    }
  };

  const [ghlTesting, setGhlTesting] = useState(false);
  const [ghlResult, setGhlResult] = useState(null);
  const sendTestGhlWebhook = async () => {
    if (ghlTesting) return;
    setGhlTesting(true);
    setGhlResult(null);
    try {
      const { data: res } = await api.post("/admin/notifications/test-ghl-webhook");
      setGhlResult(res);
    } catch (err) {
      setGhlResult({
        success: false,
        error_message: err?.response?.data?.detail || err?.message || "Request failed",
      });
    } finally {
      setGhlTesting(false);
    }
  };

  if (loading && !data) return <PortalShell title="Loading…" subtitle="Production Setup"><div className="text-slate-500">Loading readiness data…</div></PortalShell>;
  if (!data) {
    // Load finished but data is missing → show the real error + a
    // retry button. Never leave the operator on a blank page.
    return (
      <PortalShell title="Production Setup Checklist" subtitle="Admin · System">
        <div
          data-testid="admin-readiness-error"
          className="rounded-2xl border border-rose-200 bg-rose-50 p-6 max-w-2xl"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-rose-900 text-lg">Couldn't load the readiness page</div>
              <div className="mt-2 text-sm text-rose-800">
                {loadError || "The backend didn't return any data. This usually means your admin session has expired, or the frontend bundle is stale."}
              </div>
              <ol className="mt-4 space-y-1 list-decimal list-inside text-sm text-rose-700">
                <li>Hard-refresh (Ctrl / Cmd + Shift + R) to clear any cached bundle.</li>
                <li>Sign out and back in as an admin.</li>
                <li>If it still fails, open browser DevTools → Network → filter for <code className="bg-white px-1 rounded">/api/admin/readiness</code> and copy the response body — it'll tell us exactly why the endpoint refused.</li>
              </ol>
              <button
                onClick={reload}
                data-testid="admin-readiness-error-retry"
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </PortalShell>
    );
  }

  const fmt = (d) => formatDateTimeAU(d);

  return (
    <PortalShell title="Production Setup Checklist" subtitle="Admin · System">
      {/* Top-of-page live-readiness banner */}
      {data.ready_for_live ? (
        <div data-testid="readiness-banner-green" className="mb-5 rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-5 flex items-start gap-3">
          <CheckCircle2 className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold text-emerald-900">Ready For Live Operation</h2>
            <p className="text-xs text-emerald-800 mt-1">All required credentials are configured. You can flip the platform to live traffic with confidence.</p>
            <p data-testid="readiness-last-checked" className="text-[11px] text-emerald-700 mt-2 font-mono">Last checked: {fmt(data.last_checked_at)}</p>
          </div>
        </div>
      ) : (
        <div data-testid="readiness-banner-red" className="mb-5 rounded-2xl bg-rose-50 border-2 border-rose-300 p-5 flex items-start gap-3">
          <XCircle className="w-7 h-7 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-xl font-bold text-rose-900">Not Ready For Live Operation</h2>
            <p className="text-xs text-rose-800 mt-1">The following required credentials are missing or still in dev/sandbox mode:</p>
            <ul data-testid="readiness-missing-list" className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-rose-900">
              {(data.missing_credentials || []).map((k) => (
                <li key={k} data-testid={`readiness-missing-${k}`} className="flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" /> {k}
                </li>
              ))}
            </ul>
            <p data-testid="readiness-last-checked" className="text-[11px] text-rose-700 mt-3 font-mono">Last checked: {fmt(data.last_checked_at)}</p>
          </div>
        </div>
      )}

      <div className="mb-5 rounded-2xl bg-slate-900 text-white p-5 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-cyan-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-400">Go-live readiness</div>
          <h2 className="font-display text-xl font-semibold mt-1">All four integrations need a green tick before live traffic</h2>
          <p className="text-xs text-slate-300 mt-2 leading-relaxed">Webhook endpoints fail-closed by default (HTTP 400 + audit row) until their signing secrets are configured. Emails write to <code className="bg-white/10 px-1 rounded">email_logs</code> with status <code className="bg-white/10 px-1 rounded">skipped_no_key</code> until the Resend API key is set.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-7">
        <Row testId="readiness-stripe-api" ok={data.stripe_api_key_live}
          label="Stripe API key (live mode)"
          hint={data.stripe_api_key_configured && !data.stripe_api_key_live
            ? "STRIPE_API_KEY is set but still using the sandbox sentinel (sk_test_emergent). Replace with your live sk_live_… key."
            : "Set STRIPE_API_KEY to your live Stripe secret key in backend/.env."} />
        <Row testId="readiness-stripe-secret" ok={data.stripe_webhook_secret_configured}
          label="Stripe webhook secret"
          hint="Add STRIPE_WEBHOOK_SECRET to backend/.env after creating the endpoint in Stripe dashboard." />
        <Row testId="readiness-resend-key" ok={data.resend_api_key_configured}
          label="Resend API key"
          hint="Add RESEND_API_KEY after verifying lab@awarewatertesting.com.au at https://resend.com/domains" />
        <Row testId="readiness-resend-secret" ok={data.resend_webhook_secret_configured}
          label="Resend webhook secret"
          hint="Add RESEND_WEBHOOK_SECRET after adding the webhook endpoint in Resend dashboard." />
        <Row testId="readiness-google-maps" ok={data.google_maps_key_configured}
          label="Google Maps API key (frontend address autocomplete)"
          hint="Add REACT_APP_GOOGLE_MAPS_API_KEY to frontend/.env. Booking flow falls back to plain manual entry when this is empty." />
        <Row testId="readiness-llm-key" ok={data.ai_llm_key_configured}
          label="AI summary key (Claude via Emergent Universal Key)"
          hint="EMERGENT_LLM_KEY is auto-injected. Replace with your own Anthropic key if billing direct." />
      </div>

      <h3 className="text-sm font-semibold text-slate-900 mb-3">Latest signal from each integration</h3>
      <div className="grid md:grid-cols-3 gap-3 mb-7">
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500" /><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Last test email</div></div>
          {data.last_test_email ? (
            <>
              <div className="text-sm text-slate-900 mt-2 truncate" title={data.last_test_email.to}>{data.last_test_email.to}</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmt(data.last_test_email.created_at)}</div>
              <div className="mt-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${data.last_test_email.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : data.last_test_email.status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{data.last_test_email.status}</span></div>
            </>
          ) : <div className="text-xs text-slate-400 mt-2">Never sent — try the test button on /admin/emails.</div>}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2"><Webhook className="w-4 h-4 text-slate-500" /><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Last Stripe webhook</div></div>
          {data.last_stripe_webhook ? (
            <>
              <div className="text-sm text-slate-900 mt-2 truncate font-mono">{data.last_stripe_webhook.event_type || "—"}</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmt(data.last_stripe_webhook.received_at)}</div>
              <div className="mt-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${data.last_stripe_webhook.status === 'processed' ? 'bg-emerald-100 text-emerald-700' : data.last_stripe_webhook.status === 'duplicate' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{data.last_stripe_webhook.status}</span></div>
              {data.last_stripe_webhook.error && <div className="text-[10px] text-rose-600 mt-1 truncate" title={data.last_stripe_webhook.error}>{data.last_stripe_webhook.error}</div>}
            </>
          ) : <div className="text-xs text-slate-400 mt-2">No Stripe webhook events yet.</div>}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="flex items-center gap-2"><Webhook className="w-4 h-4 text-slate-500" /><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Last Resend webhook</div></div>
          {data.last_resend_webhook_event ? (
            <>
              <div className="text-sm text-slate-900 mt-2 truncate" title={data.last_resend_webhook_event.subject}>{data.last_resend_webhook_event.subject}</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmt(data.last_resend_webhook_event.delivery_updated_at)}</div>
              <div className="mt-2"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 capitalize">{data.last_resend_webhook_event.delivery_state}</span></div>
            </>
          ) : data.last_resend_webhook_failure ? (
            <>
              <div className="text-sm text-rose-700 mt-2">Signature verification failed</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmt(data.last_resend_webhook_failure.received_at)}</div>
            </>
          ) : <div className="text-xs text-slate-400 mt-2">No Resend webhook events yet.</div>}
        </div>
      </div>

      <h3 className="text-sm font-semibold text-slate-900 mb-3">Configuration reference</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <Detail title="Sender email" value={data.sender_email} status={data.email_configured ? "ok" : "warn"} />
        <Detail title="Email pipeline" value={data.email_configured ? "Live — sends through Resend" : "Skipped — logged but not sent"} status={data.email_configured ? "ok" : "warn"} />
        <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-cyan-300 transition-colors group">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-600 inline-flex items-center gap-1.5"><Key className="w-3 h-3" />Open Stripe webhooks <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60" /></div>
          <div className="text-xs text-slate-600 mt-1">Add endpoint URL: <code className="bg-slate-100 px-1 rounded font-mono break-all">/api/webhook/stripe</code></div>
        </a>
        <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-cyan-300 transition-colors group">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-600 inline-flex items-center gap-1.5"><Key className="w-3 h-3" />Verify Resend domain <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60" /></div>
          <div className="text-xs text-slate-600 mt-1">Then add webhook endpoint: <code className="bg-slate-100 px-1 rounded font-mono break-all">/api/webhook/resend</code></div>
        </a>
      </div>

      <div className="mt-7 rounded-2xl bg-cyan-50 border border-cyan-200 p-5 flex items-start gap-3">
        <Brain className="w-5 h-5 text-cyan-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-cyan-900">AI summaries are ready</div>
          <div className="text-xs text-cyan-800 mt-1">Open any published test in <a href="/admin/lab" className="underline font-semibold">/admin/lab</a> and click <strong>&ldquo;Generate AI summary&rdquo;</strong> to draft a plain-English customer-facing narrative powered by Claude Sonnet 4.6.</div>
        </div>
      </div>

      {/* Admin-controlled platform settings */}
      <div data-testid="platform-settings-card" className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900">Lab tech permissions</h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">Default policy is that only admins can approve and send final reports. Toggle the switch below to let lab technicians approve and send reports without admin review. Reports flagged with <code className="bg-slate-100 px-1 rounded text-[10px]">requires_stephen_review</code> always remain admin-only, regardless of this setting.</p>
        <label className="mt-3 flex items-center justify-between gap-4 cursor-pointer">
          <span className="text-sm text-slate-700">Allow Lab Tech to approve and send customer reports</span>
          <button
            data-testid="toggle-lab-tech-approve"
            type="button"
            disabled={settingsSaving}
            onClick={() => togglePermission("lab_tech_can_approve_and_send_reports", !platformSettings?.lab_tech_can_approve_and_send_reports)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${platformSettings?.lab_tech_can_approve_and_send_reports ? "bg-cyan-600" : "bg-slate-300"} disabled:opacity-50`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${platformSettings?.lab_tech_can_approve_and_send_reports ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </label>
        <div className="mt-2 text-[11px] text-slate-500">Current: <strong className={platformSettings?.lab_tech_can_approve_and_send_reports ? "text-emerald-700" : "text-slate-700"}>{platformSettings?.lab_tech_can_approve_and_send_reports ? "Lab techs CAN approve & send" : "Admin-only approval (default)"}</strong></div>
      </div>

      {/* Bank-transfer details for invoice bookings (Phase 2L-A7).
          Admin-only writes; lab tech can read. Used by the customer-facing
          invoice email template — invoice bookings are REFUSED at the
          backend until these three required fields are populated. */}
      <div data-testid="bank-details-card" className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <Landmark className="w-5 h-5 text-cyan-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900">Bank transfer details for invoice bookings</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Shown to customers in the <strong>invoice</strong> email when staff create a booking with <code className="bg-slate-100 px-1 rounded text-[10px]">payment_mode = invoice</code>. Lab tech can read these values; only admin can edit. Invoice bookings are blocked until Account name, BSB and Account number are filled.
            </p>
            <span className={`mt-2 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${bankConfigured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {bankConfigured ? "Configured" : "Not configured — invoice bookings will be blocked"}
            </span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-700">
            Account name
            <input data-testid="bank-account-name"
              value={bankForm.bank_account_name}
              onChange={(e) => setBankForm({ ...bankForm, bank_account_name: e.target.value })}
              placeholder="e.g. Pristine Water Systems"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal focus:border-cyan-500 focus:outline-none" />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Bank name <span className="text-slate-400 font-normal">(optional)</span>
            <input data-testid="bank-name"
              value={bankForm.bank_name}
              onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
              placeholder="e.g. ANZ"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal focus:border-cyan-500 focus:outline-none" />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            BSB
            <input data-testid="bank-bsb"
              value={bankForm.bank_bsb}
              onChange={(e) => setBankForm({ ...bankForm, bank_bsb: e.target.value })}
              placeholder="6 digits"
              inputMode="numeric"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-cyan-500 focus:outline-none" />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Account number
            <input data-testid="bank-account-number"
              value={bankForm.bank_account_number}
              onChange={(e) => setBankForm({ ...bankForm, bank_account_number: e.target.value })}
              placeholder="6-10 digits"
              inputMode="numeric"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-cyan-500 focus:outline-none" />
          </label>
          <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
            Payment reference instruction
            <input data-testid="bank-payment-reference"
              value={bankForm.bank_payment_reference}
              onChange={(e) => setBankForm({ ...bankForm, bank_payment_reference: e.target.value })}
              placeholder="Please use your booking reference as the payment reference, e.g. SCWT-123456."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal focus:border-cyan-500 focus:outline-none" />
          </label>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-[11px] text-slate-500">
            Values are stored in the platform settings document and never bundled into the frontend.
          </span>
          <div className="flex items-center gap-2">
            {bankSaveOk && <span className="text-xs font-semibold text-emerald-700">Saved ✓</span>}
            <button
              data-testid="bank-details-save"
              type="button"
              onClick={saveBank}
              disabled={bankSaving}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {bankSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Save bank details
            </button>
          </div>
        </div>
      </div>

      {/* AI Summary guardrails on-demand check */}
      <AiSummaryGuardrailsPanel
        guardrails={guardrails}
        running={guardrailsRunning}
        onRun={runGuardrailsCheck}
        fmt={fmt}
      />

      {/* Live deliverability smoke test — fires the real booking-confirmation
          template + Resend pipeline against an admin-chosen recipient.
          Logs to email_logs alongside real sends. */}
      <div data-testid="test-booking-email-panel" className="mt-5 rounded-2xl bg-white border-2 border-slate-200 p-5">
        <div className="flex items-start gap-3 mb-4">
          <Send className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Live booking-email smoke test</h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">Fires a realistic booking confirmation using the exact same template and Resend pipeline a real customer would receive. Subject line is prefixed with <code className="bg-slate-100 px-1 rounded text-[11px]">TEST</code>. Result is logged to <a href="/admin/emails" className="underline font-medium">/admin/emails</a> with the full Resend response/error.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="test-booking-email-input"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 px-3 py-2 rounded-xl border border-slate-300 text-sm font-mono focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            disabled={testSending}
          />
          <button
            data-testid="test-booking-email-send-btn"
            onClick={sendTestBookingConfirmation}
            disabled={!testTo || testSending}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send test booking confirmation</>}
          </button>
        </div>
        {testResult && (
          <div
            data-testid={testResult.sent ? "test-booking-email-success" : "test-booking-email-failure"}
            className={`mt-4 rounded-xl border p-3 text-xs ${testResult.sent ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
          >
            <div className="flex items-start gap-2">
              {testResult.sent
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />}
              <div className="flex-1 space-y-1">
                <div className="font-semibold">
                  {testResult.sent ? "Test email sent successfully" : "Test email failed"}
                </div>
                {testResult.subject && (
                  <div><span className="font-medium">Subject:</span> {testResult.subject}</div>
                )}
                {testResult.from && (
                  <div><span className="font-medium">From:</span> <span className="font-mono">{testResult.from}</span></div>
                )}
                {testResult.to && (
                  <div><span className="font-medium">To:</span> <span className="font-mono">{testResult.to}</span></div>
                )}
                {testResult.status && (
                  <div><span className="font-medium">Resend status:</span> <span className="font-mono">{testResult.status}</span></div>
                )}
                {testResult.provider_id && (
                  <div><span className="font-medium">Provider ID:</span> <span className="font-mono break-all">{testResult.provider_id}</span></div>
                )}
                {testResult.error && (
                  <div><span className="font-medium">Error:</span> <span className="font-mono break-all">{testResult.error}</span></div>
                )}
                {!testResult.configured && (
                  <div className="mt-1 italic">RESEND_API_KEY is not configured — the send was logged as <code className="bg-white/60 px-1 rounded">skipped_no_key</code>.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GHL staff-SMS webhook test (Phase 2L-A9).
          Fires a synthetic new_booking_received payload to the configured
          GoHighLevel inbound webhook so admins can confirm staff SMS is
          wired correctly. Ignores ENABLE_GHL_BOOKING_SMS_ALERTS but still
          requires GHL_NEW_BOOKING_STAFF_SMS_WEBHOOK_URL. */}
      <div data-testid="ghl-webhook-test-panel" className="mt-5 rounded-2xl bg-white border-2 border-slate-200 p-5">
        <div className="flex items-start gap-3 mb-4">
          <Webhook className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900">GoHighLevel staff-SMS webhook test</h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Sends a synthetic <code className="bg-slate-100 px-1 rounded text-[11px]">new_booking_received</code> payload to your GHL inbound webhook so you can validate the staff SMS trigger. The customer is never contacted — GHL fans this out as an internal staff SMS only. Outcome is logged to <code className="bg-slate-100 px-1 rounded text-[11px]">notification_logs</code>.
            </p>
          </div>
        </div>
        <button
          data-testid="ghl-webhook-test-btn"
          onClick={sendTestGhlWebhook}
          disabled={ghlTesting}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {ghlTesting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send test GHL booking alert</>}
        </button>
        {ghlResult && (
          <div
            data-testid={ghlResult.success ? "ghl-webhook-test-success" : "ghl-webhook-test-failure"}
            className={`mt-4 rounded-xl border p-3 text-xs ${ghlResult.success ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
          >
            <div className="flex items-start gap-2">
              {ghlResult.success
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />}
              <div className="flex-1 space-y-1">
                <div className="font-semibold">
                  {ghlResult.success ? "Test webhook delivered to GHL" : "Test webhook failed"}
                </div>
                {ghlResult.response_status != null && (
                  <div><span className="font-medium">HTTP status:</span> <span className="font-mono">{ghlResult.response_status}</span></div>
                )}
                {ghlResult.mode && (
                  <div><span className="font-medium">Mode:</span> <span className="font-mono">{ghlResult.mode}</span></div>
                )}
                {ghlResult.created_at && (
                  <div><span className="font-medium">At:</span> <span className="font-mono">{fmt(ghlResult.created_at)}</span></div>
                )}
                {ghlResult.error_message && (
                  <div><span className="font-medium">Error:</span> <span className="font-mono break-all">{ghlResult.error_message}</span></div>
                )}
                <div className="mt-1 italic text-[11px] opacity-80">Logged as a row in <code className="bg-white/60 px-1 rounded">notification_logs</code>. Check your GHL workflow for the inbound trigger.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}



/**
 * Small on-demand panel that runs the AI Summary guardrails check against
 * synthetic payloads (high-priority + clean) and surfaces a green/red/grey
 * pip plus plain-English failure messages for any broken guardrails.
 *
 * States:
 *   passing      → green pip
 *   failing      → red pip + list of failed checks
 *   error        → red pip + error string (e.g. Claude rate limit, JSON parse)
 *   unavailable  → grey pip (EMERGENT_LLM_KEY missing)
 *   not_checked  → grey pip ("Click Run check to test")
 */
function AiSummaryGuardrailsPanel({ guardrails, running, onRun, fmt }) {
  const status = guardrails?.status || "not_checked";
  const TONE = {
    passing:     { wrap: "bg-emerald-50 border-emerald-200", icon: CheckCircle2, color: "text-emerald-600", pill: "bg-emerald-200 text-emerald-900", label: "Passing" },
    failing:     { wrap: "bg-rose-50 border-rose-200",       icon: XCircle,      color: "text-rose-600",    pill: "bg-rose-200 text-rose-900",       label: "Failing" },
    error:       { wrap: "bg-rose-50 border-rose-200",       icon: XCircle,      color: "text-rose-600",    pill: "bg-rose-200 text-rose-900",       label: "Error" },
    unavailable: { wrap: "bg-slate-50 border-slate-200",     icon: CircleDashed, color: "text-slate-400",   pill: "bg-slate-200 text-slate-700",     label: "Unavailable" },
    not_checked: { wrap: "bg-slate-50 border-slate-200",     icon: CircleDashed, color: "text-slate-400",   pill: "bg-slate-200 text-slate-700",     label: "Not checked" },
  }[status] || { wrap: "bg-slate-50 border-slate-200", icon: CircleDashed, color: "text-slate-400", pill: "bg-slate-200 text-slate-700", label: status };

  const Icon = TONE.icon;
  const summary = guardrails && guardrails.total_passed + guardrails.total_failed > 0
    ? `${guardrails.total_passed}/${guardrails.total_passed + guardrails.total_failed} checks passed`
    : null;

  return (
    <div data-testid="ai-guardrails-panel" className={`mt-5 rounded-2xl border ${TONE.wrap} p-5`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${TONE.color}`} />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-slate-900">AI Summary Guardrails</h3>
            <span data-testid="ai-guardrails-pip" className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${TONE.pill}`}>{TONE.label}</span>
            {summary && <span className="text-[11px] text-slate-500">{summary}</span>}
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            On-demand regression check that runs the customer-facing AI Summary against synthetic high-priority and clean payloads. Catches alarmist wording, missing Positive/Negative microbiology wording, mismatched <code className="bg-white/60 px-1 rounded text-[10px]">rule_key</code>s, oversized narratives and JSON-shape drift — before a real customer summary is generated. No customer data used, nothing published.
          </p>
          {guardrails?.checked_at && (
            <p data-testid="ai-guardrails-last-checked" className="text-[11px] text-slate-500 mt-1.5">
              Last checked: {fmt(guardrails.checked_at)} · model {guardrails.model || "—"}
            </p>
          )}

          {(status === "failing" || status === "error") && (
            <div className="mt-3 rounded-xl bg-white border border-rose-200 p-3 text-xs">
              <div className="font-semibold text-rose-900 mb-1.5">{guardrails?.error ? "Generation error" : "Failed guardrails"}</div>
              {guardrails?.error ? (
                <div data-testid="ai-guardrails-error" className="font-mono text-rose-800 break-all">{guardrails.error}</div>
              ) : (
                <ul data-testid="ai-guardrails-failed-list" className="space-y-1 text-rose-900">
                  {(guardrails.failed_checks || []).map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <XCircle className="w-3 h-3 text-rose-500 flex-shrink-0 mt-0.5" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {status === "passing" && guardrails?.checks?.length > 0 && (
            <details className="mt-3 group">
              <summary className="cursor-pointer text-xs text-slate-600 hover:text-slate-900 select-none">
                Show {guardrails.checks.length} passing checks
              </summary>
              <ul data-testid="ai-guardrails-passing-list" className="mt-2 grid sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-700">
                {guardrails.checks.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span><span className="text-slate-400">[{c.scenario}]</span> {c.name}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              data-testid="ai-guardrails-run-btn"
              onClick={onRun}
              disabled={running || status === "unavailable"}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {running
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running… (~25s)</>
                : <><PlayCircle className="w-3.5 h-3.5" /> Run guardrails check</>}
            </button>
            {status === "unavailable" && (
              <span className="text-[11px] text-slate-500 italic">EMERGENT_LLM_KEY not configured — AI summaries are disabled.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
